import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store/store';

interface PermissionRouteProps {
  /** The permission(s) required to access this route. If array, ANY permission grants access. */
  requiredPermission: string | string[];
  /** The component to render if user has permission */
  children: React.ReactNode;
  /** Where to redirect if user doesn't have permission (default: /kontrol) */
  redirectTo?: string;
}

/**
 * A wrapper component that checks if the user has the required permission(s)
 * before rendering the children. If not, redirects to the specified route.
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  requiredPermission,
  children,
  redirectTo = '/kontrol',
}) => {
  const userPermissions = useAppSelector(
    (state: RootState) => state.userProfile.ListPermiso || []
  );
  const userId = useAppSelector(
    (state: RootState) => state.userProfile.id
  );

  const required = Array.isArray(requiredPermission)
    ? requiredPermission
    : [requiredPermission];

  // If user profile hasn't loaded yet (e.g. after page refresh), wait instead of redirecting
  if (!userId && userPermissions.length === 0) {
    return null;
  }

  const hasPermission = required.some((p) => userPermissions.includes(p));

  if (!hasPermission) {
    console.warn(
      `[PermissionRoute] Access denied. Required: ${required.join(' OR ')}, User has: ${userPermissions.join(', ')}`
    );
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PermissionRoute;

